/**
 * Cercevo: persistent 360°/3D corner buttons on the product media gallery.
 *
 * Two small pill buttons pinned to the gallery's bottom-right corner, similar
 * to elephantstock.com's "VIEW IN MY ROOM"/"360°" buttons. Added because the
 * theme's native 3D model is otherwise just the Nth (usually last) gallery
 * slide — visitors don't realize it's there or that it's interactive.
 *
 * - "360°" (bottom): jumps the gallery to the 3D model slide AND clicks its
 *   deferred-media play button, so the model is immediately interactive
 *   instead of merely scrolled into view still showing its static poster.
 *   Uses MediaGallery's public `slideshow` getter (assets/media-gallery.js)
 *   to call Slideshow#select(index) (assets/slideshow.js) — the same method
 *   the theme's own dot/thumbnail navigation calls internally. `select()`
 *   only scrolls; it does not itself trigger the deferred-media "play" for
 *   <product-model> (that's the separate button
 *   ref="deferredMediaPlayButton" in snippets/product-media.liquid), so this
 *   also finds and .click()s that button on the target slide.
 * - "3D" (above it): scrolls down to the standalone AR badge block
 *   (blocks/cercevo-ar-note.liquid, [data-cercevo-ar-note]) and adds a
 *   temporary spotlight/twinkle highlight so the visitor notices it, since
 *   that badge lives below the gallery in the product-details column and can
 *   otherwise go unnoticed.
 *
 * Rendered (and this script only loaded) when the product has a 3D model —
 * see the `first_3d_model` gate in snippets/product-media-gallery-content.liquid.
 */

const CERCEVO_AR_HIGHLIGHT_CLASS = 'cercevo-ar-box--spotlight';
const CERCEVO_AR_HIGHLIGHT_DURATION_MS = 2200;

class CercevoGallery3dButtons extends HTMLElement {
  connectedCallback() {
    const gotoSlideBtn = this.querySelector('[data-cercevo-3d-goto-slide]');
    const scrollArBtn = this.querySelector('[data-cercevo-3d-scroll-ar]');

    if (gotoSlideBtn) {
      gotoSlideBtn.addEventListener('click', () => this.goToSlide());
    }

    if (scrollArBtn) {
      scrollArBtn.addEventListener('click', () => this.scrollToArBadge());
    }
  }

  goToSlide() {
    const slideIndexAttr = this.dataset.slideIndex;
    const gridIndexAttr = this.dataset.gridIndex;
    const slideIndex = slideIndexAttr != null ? parseInt(slideIndexAttr, 10) : NaN;
    const gridIndex = gridIndexAttr != null ? parseInt(gridIndexAttr, 10) : NaN;

    const mediaGallery = this.closest('media-gallery');
    if (!mediaGallery) return;

    // Carousel presentation (and grid-on-mobile): the slideshow-component
    // drives navigation and is the visible element.
    const slideshow = /** @type {any} */ (mediaGallery).slideshow;
    if (slideshow && !Number.isNaN(slideIndex)) {
      slideshow.select(slideIndex, undefined, { animate: true });

      const slides = mediaGallery.querySelectorAll('slideshow-slide');
      const targetSlide = slides[slideIndex];
      const playButton = targetSlide?.querySelector('[ref="deferredMediaPlayButton"]');
      if (playButton instanceof HTMLElement) playButton.click();
    }

    // Grid presentation on desktop: the slideshow-component is display:none
    // and the static grid list is what's visible, so also activate + scroll
    // to the matching item there.
    if (!Number.isNaN(gridIndex)) {
      const gridItems = mediaGallery.querySelectorAll('.media-gallery__grid [ref="media[]"]');
      const gridItem = gridItems[gridIndex];
      if (gridItem instanceof HTMLElement) {
        const gridPlayButton = gridItem.querySelector('[ref="deferredMediaPlayButton"]');
        if (gridPlayButton instanceof HTMLElement) gridPlayButton.click();
        gridItem.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }

  scrollToArBadge() {
    const arNote = document.querySelector('[data-cercevo-ar-note]');
    if (!arNote) return;

    arNote.scrollIntoView({ behavior: 'smooth', block: 'center' });
    arNote.classList.add(CERCEVO_AR_HIGHLIGHT_CLASS);
    window.setTimeout(() => {
      arNote.classList.remove(CERCEVO_AR_HIGHLIGHT_CLASS);
    }, CERCEVO_AR_HIGHLIGHT_DURATION_MS);
  }
}

if (!customElements.get('cercevo-gallery-3d-buttons')) {
  customElements.define('cercevo-gallery-3d-buttons', CercevoGallery3dButtons);
}
